import {CARRIER_SERVICE_LEVELS} from './carrier-service-levels';

/**
 * The shipping choices the EcommerceOrderDetails panel offers an operator, and
 * the rule that the list is never empty.
 *
 * ## Why this is a module and not a method
 *
 * It is the only part of that panel that can be wrong in a way nobody notices
 * until an order is unfixable, and it is pure: facility quotes in, options out.
 * Keeping it out of the component is what lets
 * `tests/CarrierServiceLevelOptions.smoke.ts` run it under plain ts-node
 * without dragging Angular in.
 */

/**
 * Where the entries in the list came from -- i.e. how far the builder had to
 * degrade to keep the list populated.
 *
 * This is also exactly what the operator must be told about the prices shown
 * beside each entry, which is why it is returned rather than kept private.
 *
 *   FACILITY        priced quotes for the facility this order will ship from.
 *                   The normal case; the prices are real
 *   ALL_FACILITIES  no facility is settled yet, so the quotes of every
 *                   candidate facility are pooled. The prices are indicative
 *   CATALOG         nothing was ever rated, so the list is the static
 *                   CARRIER_SERVICE_LEVELS catalogue and there are no prices
 */
export type CarrierServiceLevelOptionSource = 'FACILITY' | 'ALL_FACILITIES' | 'CATALOG';

export class CarrierServiceLevelOption {
  /**
   * The value the mat-option carries and the form control holds -- always
   * `carrier + '_' + serviceLevel`, which is also the shape of
   * `EcommerceOrder.shippingInfo` and of `CARRIER_SERVICE_LEVELS[].ID`.
   *
   * Held as a field rather than recomposed in the template because a service
   * level may itself contain an underscore (`PRIORITY_FLAT`), so the old
   * `value.split('_')[0] / [1]` could not take the key apart again.
   */
  id: string;
  carrier: string;
  serviceLevel: string;
  /** Meaningless unless `costKnown`; kept at 0 so arithmetic never sees NaN. */
  cost: number;
  /**
   * False for an entry we can offer but cannot price -- a catalogue entry, or
   * the unreadable value that arrived with the order. The template shows a word
   * instead of `$0.00`, and `saveCarrierService()` leaves the order's existing
   * `shippingCost` alone rather than inventing one.
   */
  costKnown: boolean;
  /**
   * True only for the synthetic entry that echoes an incoming carrier/service
   * level we could not interpret.
   *
   * It exists so the box is not blank: MatSelect renders its trigger from the
   * *selected option*, so a control value matching no option reads as no
   * selection and the operator cannot even see what the store sent. It is
   * rendered disabled, because re-choosing it is the one thing that cannot
   * work.
   */
  unrecognized: boolean;
}

export interface CarrierServiceLevelOptionList {
  options: CarrierServiceLevelOption[];
  source: CarrierServiceLevelOptionSource;
}

export function carrierServiceKey(carrier: string, serviceLevel: string): string {
  return carrier + '_' + serviceLevel;
}

/**
 * First segment of a `carrier_serviceLevel` key.
 *
 * The split is on the FIRST underscore only, because service levels contain
 * underscores of their own (`USPS_PRIORITY_FLAT`). The template used to do
 * `split('_')[0]` / `[1]` inline, and so rendered that one as "USPS PRIORITY"
 * -- silently naming a different, cheaper service.
 */
export function carrierOfKey(id: string): string {
  const separator = id.indexOf('_');
  return separator < 0 ? id : id.substring(0, separator);
}

export function serviceLevelOfKey(id: string): string {
  const separator = id.indexOf('_');
  return separator < 0 ? '' : id.substring(separator + 1);
}

/**
 * Build the list the Shipping combo box offers, and never return an empty one.
 *
 * ## The bug this exists to kill
 *
 * The priced options live in `EcommerceOrder.facilityOptions`, which the server
 * fills in when it rates the order, keyed to the facility it picked. The panel
 * used to read only the entry whose `facilityId` equalled
 * `bookorder.facilityId` -- correct for a healthy order, and exactly wrong for
 * the only kind of order an operator ever opens the box for.
 *
 * When the carrier/service level that arrived from the remote store is not one
 * we understand, `carrierServiceLevelStatus` is `ERROR` and the order never
 * reaches a rating: `facilityOptions` is empty, or no facility is settled so
 * `bookorder.facilityId` is 0/absent and nothing matches. Either way the loop
 * produced zero entries, the box opened with nothing in it, and the one
 * correction that would release the order could not be made. The order was
 * stuck for good, with no way out through the UI.
 *
 * ## So the list degrades instead of vanishing
 *
 * 1. the chosen facility's quotes (`FACILITY`) -- unchanged behaviour,
 * 2. else every candidate facility's quotes pooled (`ALL_FACILITIES`),
 *    deduplicated on carrier/service level, keeping the **cheapest** quote so
 *    the figure shown is never worse than what the order is finally charged
 *    once a facility is settled,
 * 3. else the static `CARRIER_SERVICE_LEVELS` catalogue, with no prices at all
 *    (`CATALOG`).
 *
 * Then, whatever the source, `incomingShippingInfo` is appended as a disabled
 * entry when it matches nothing in the list -- see
 * `CarrierServiceLevelOption.unrecognized`.
 *
 * @param facilityOptions      `EcommerceOrder.facilityOptions` as the server
 *                             sent it; any shape of junk inside is tolerated
 * @param facilityId           `bookorder.facilityId`; 0, null or an id no
 *                             facilityOption mentions all mean "not settled"
 * @param incomingShippingInfo `EcommerceOrder.shippingInfo` -- the value on the
 *                             order now, recognized or not
 */
export function buildCarrierServiceLevelOptions(
  facilityOptions: any[] | null | undefined,
  facilityId: any,
  incomingShippingInfo: string | null | undefined ): CarrierServiceLevelOptionList {

  const rated = facilityOptions || [];
  const chosenFacilityId = Number(facilityId);

  // A facility counts as settled only if it is a real id AND one of the rated
  // facilities names it. A stale id left on the bookorder that no facilityOption
  // mentions has to fall through to the pooled list, not to an empty one.
  const hasChosenFacility = !!chosenFacilityId && !isNaN(chosenFacilityId)
    && rated.some( facilityOption => facilityOption && Number(facilityOption.facilityId) === chosenFacilityId );

  let options: CarrierServiceLevelOption[] = [];
  let source: CarrierServiceLevelOptionSource = 'CATALOG';

  if ( hasChosenFacility ) {
    options = pricedCarrierServiceLevelOptions(
      rated.filter( facilityOption => facilityOption && Number(facilityOption.facilityId) === chosenFacilityId ) );
    source = 'FACILITY';
  }
  if ( options.length === 0 && rated.length > 0 ) {
    options = pricedCarrierServiceLevelOptions(rated);
    source = 'ALL_FACILITIES';
  }
  if ( options.length === 0 ) {
    options = catalogCarrierServiceLevelOptions();
    source = 'CATALOG';
  }

  options.sort( ( a, b ) => a.carrier.localeCompare(b.carrier) || a.serviceLevel.localeCompare(b.serviceLevel) );

  appendIncomingIfUnrecognized(options, incomingShippingInfo);

  return { options, source };
}

/**
 * Collapse the rated facilities' per-package quotes into one priced entry per
 * carrier/service level.
 *
 * Cost is summed across the packages of a single facility -- the order ships as
 * all of them -- but *minimised* across facilities, because the order will ship
 * from exactly one of them and the cheapest is the only honest figure to show
 * while that choice is still open.
 *
 * The accumulator is per call, not shared across facilities as it used to be:
 * the old code declared it outside the facility loop and pushed inside it, so
 * two `facilityOptions` entries sharing a `facilityId` emitted every option
 * twice.
 */
function pricedCarrierServiceLevelOptions(facilityOptions: any[]): CarrierServiceLevelOption[] {
  const costById: { [id: string]: number } = {};
  for ( const facilityOption of facilityOptions ) {
    if ( !facilityOption || !facilityOption.packages ) {
      continue;
    }
    const facilityCostById: { [id: string]: number } = {};
    for ( const pkg of facilityOption.packages ) {
      if ( !pkg || !pkg.shippingOptions ) {
        continue;
      }
      const numberOfPackages = pkg.number ? pkg.number : 1;
      for ( const shippingOption of pkg.shippingOptions ) {
        if ( !shippingOption || !shippingOption.c || !shippingOption.s ) {
          continue;
        }
        const id = carrierServiceKey(shippingOption.c, shippingOption.s);
        const cost = Number(shippingOption.cost) * numberOfPackages;
        facilityCostById[id] = ( facilityCostById[id] === undefined ? 0 : facilityCostById[id] ) + ( isNaN(cost) ? 0 : cost );
      }
    }
    for ( const id of Object.keys(facilityCostById) ) {
      if ( costById[id] === undefined || facilityCostById[id] < costById[id] ) {
        costById[id] = facilityCostById[id];
      }
    }
  }
  return Object.keys(costById).map( id => makeOption(id, carrierOfKey(id), serviceLevelOfKey(id), costById[id], true, false) );
}

/**
 * The whole catalogue, unpriced -- the last resort that keeps the box usable
 * for an order that was never rated at all.
 *
 * Offering a service level the facility may not actually sell is the lesser
 * evil: the operator's choice is written back and the server's `compute()`
 * re-rates the order, which is where an impossible combination is caught and
 * reported. An empty box catches nothing and reports nothing -- it simply ends
 * the operator's options.
 */
function catalogCarrierServiceLevelOptions(): CarrierServiceLevelOption[] {
  return CARRIER_SERVICE_LEVELS.map( carrierServiceLevel =>
    makeOption(carrierServiceLevel.ID, carrierServiceLevel.carrier, carrierServiceLevel.serviceLevel, 0, false, false) );
}

/**
 * Make the value that arrived visible even when we cannot interpret it.
 *
 * Appended rather than sorted in, so it reads as what it is: the thing on the
 * order now, sitting underneath the list of things it could become.
 */
function appendIncomingIfUnrecognized(options: CarrierServiceLevelOption[], incomingShippingInfo: string | null | undefined): void {
  const incoming = incomingShippingInfo ? String(incomingShippingInfo).trim() : '';
  if ( incoming === '' ) {
    return;
  }
  if ( options.some( option => option.id === incoming ) ) {
    return;
  }
  options.push( makeOption(incoming, carrierOfKey(incoming), serviceLevelOfKey(incoming), 0, false, true) );
}

function makeOption(id: string, carrier: string, serviceLevel: string, cost: number, costKnown: boolean, unrecognized: boolean): CarrierServiceLevelOption {
  const option = new CarrierServiceLevelOption();
  option.id = id;
  option.carrier = carrier;
  option.serviceLevel = serviceLevel;
  option.cost = cost;
  option.costKnown = costKnown;
  option.unrecognized = unrecognized;
  return option;
}

